import React, { useState, useRef } from "react";
import {
  Alert,
  Box,
  Button,
  Collapse,
  Link,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import SaveIcon from "@mui/icons-material/Save";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Authenticator from "components/Authenticator";
import { useStore } from "outstated";
import { AppStore } from "pages/_app.js";
import handlePDFDownload from "components/Calculators/components/SaveSharePDF/components/handlePDFDownload";
import useMediaQuery from "@mui/material/useMediaQuery";
import saveOrUpdate from "components/Calculators/components/utils/saveOrUpdate";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import updateLoan2 from "lib/updateLoan2";
import dynamic from "next/dynamic";
const MUIRichTextEditor = dynamic(() => import("mui-rte"), { ssr: false });
import {
  EditorState,
  convertToRaw,
  convertFromHTML,
  ContentState,
} from "draft-js";
import draftToHtml from "draftjs-to-html";
import getLoanTermsUpdateEmailTemplate from "lib/getLoanTermsUpdateEmailTemplate";
import EmailEditor from "components/global/EmailEditor";
import sleep from "lib/sleep";

const SaveSharePDF = ({
  answers,
  allowedQuestionTitles, // this is to control which questions are visible by title
  generateAnswerOptions,
  calc,
  setCalc,
  calculatorType,
  loanTermsUI = false,
  loan,
  onVersionSelect,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const {
    showLogInDialog,
    setShowLogInDialog,
    user,
    userProfile,
    snackbarData,
    setSnackbarData,
    userPermissions,
    userRoles,
  } = useStore(AppStore);
  const [showShareFields, setShowShareFields] = useState(false);
  // const [showLogInDialog, setShowLogInDialog] = useState(false);
  const [showUpdateFields, setShowUpdateFields] = useState(false);
  const [showCreatedSuccessMessage, setShowCreatedSuccessMessage] =
    useState(false);
  const [showCopiedSuccess, setShowCopiedSuccess] = useState(false);
  const [showExplanationField, setShowExplanationField] = useState(false);
  const [explanationText, setExplanationText] = useState("");
  const [editorHtml, setEditorHtml] = useState("");
  const [loadingTemplate, setLoadingTemplate] = useState(false);

  const [comment, setComment] = useState("");

  const initialEditorState = useRef(
    JSON.stringify(
      convertToRaw(
        ContentState.createFromText(
          "Explanation of Loan Term changes:\n1. \n2. \n3. "
        )
      )
    )
  );

  const calcType = calculatorType;

  return (
    <Box>
      {!loanTermsUI && (
        <>
          <Box sx={{ fontSize: theme.spacing(3), fontWeight: 600 }}>
            Get PDF, save or share?
          </Box>

          <Typography
            component="h2"
            sx={{
              display: "flex",
              fontSize: theme.spacing(1.5),
              margin: theme.spacing(0, 0, 0, "auto"),
              [theme.breakpoints.down("md")]: {
                display: "block",
              },
            }}
          >
            *All your calculations will be stored in{" "}
            <Link
              sx={{
                margin: theme.spacing(0, 0, 0, 0.5),
                color: theme.palette.highContrastLink.main,
              }}
              rel="noopener"
              target="_blank"
              href="/my/calculations"
            >
              {" "}
              My Calculations
            </Link>
          </Typography>
        </>
      )}
      {userProfile && Object.keys(userProfile).length === 0 && (
        <Box
          sx={{
            margin: theme.spacing(2, 0, 0, 0),
            display: "flex",
            flexDirection: "row",
            gap: theme.spacing(2),
            justifyContent: "space-between",
            position: "relative",
            height: theme.spacing(8),
            transformStyle: "preserve-3d",
            [theme.breakpoints.down("md")]: {
              flexDirection: "column",
              height: "auto",
              gap: theme.spacing(2.5),
              alignItems: "center",
            },
          }}
        >
          <Button
            onClick={() => {
              setShowLogInDialog(!showLogInDialog);
            }}
            color="info"
            variant={"contained"}
            sx={{
              flex: 1,
              fontWeight: 700,
              boxShadow: 2,
              transform: "none",
              height: theme.spacing(6.5),
              transformOrigin: "bottom center",
              transition: "box-shadow 0.3s ease",
              "&:hover": {
                transform: "none",
                boxShadow: 3,
                zIndex: 2,
                filter: "brightness(1.05)",
                borderWidth: "2px",
              },
              [theme.breakpoints.down("md")]: {
                width: "66%",
                margin: theme.spacing(3, "auto", 0, "auto"),
                py: 1.5,
              },
              [theme.breakpoints.down("sm")]: {
                minHeight: theme.spacing(7),
              },
            }}
          >
            {
              <Box
                sx={{
                  padding: theme.spacing(0.8),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                }}
              >
                <PictureAsPdfIcon
                  sx={{ mr: 1, fontSize: { xs: "1.4rem", sm: "1.2rem" } }}
                />
                PDF
              </Box>
            }
          </Button>
          <Button
            color={"primary"}
            variant={"outlined"}
            sx={{
              flex: 1,
              fontWeight: 700,
              boxShadow: 1,
              transform: "none",
              height: theme.spacing(6.5),
              transformOrigin: "bottom center",
              transition: "box-shadow 0.3s ease",
              "&:hover": {
                transform: "none !important",
                boxShadow: 1,
                zIndex: 1,
                filter: "brightness(1.05)",
                borderWidth: "1px !important",
              },
              [theme.breakpoints.down("md")]: {
                width: "66%",
                margin: theme.spacing(3, "auto", 0, "auto"),
                py: 1.5,
              },
              [theme.breakpoints.down("sm")]: {
                minHeight: theme.spacing(7),
              },
            }}
            onClick={() => {
              setShowLogInDialog(!showLogInDialog);
            }}
          >
            {
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    padding: theme.spacing(0.8),
                    fontWeight: 700,
                  }}
                >
                  <ShareOutlinedIcon
                    sx={{ mr: 1, fontSize: { xs: "1.4rem", sm: "1.2rem" } }}
                  />
                  Share
                </Box>
              </Box>
            }
          </Button>
          <Button
            color="success"
            variant={"contained"}
            sx={{
              flex: 1,
              fontWeight: 700,
              boxShadow: 2,
              transform: "none",
              height: theme.spacing(6.5),
              transformOrigin: "bottom center",
              transition: "box-shadow 0.3s ease",
              "&:hover": {
                transform: "none",
                boxShadow: 3,
                zIndex: 2,
                filter: "brightness(1.05)",
                borderWidth: "2px",
              },
              [theme.breakpoints.down("md")]: {
                width: "66%",
                margin: theme.spacing(3, "auto", 0, "auto"),
                py: 1.5,
              },
              [theme.breakpoints.down("sm")]: {
                minHeight: theme.spacing(7),
              },
            }}
            onClick={() => {
              setShowLogInDialog(!showLogInDialog);
            }}
          >
            {
              <Box
                sx={{
                  padding: theme.spacing(0.8),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                }}
              >
                <SaveIcon
                  sx={{ mr: 1, fontSize: { xs: "1.4rem", sm: "1.2rem" } }}
                />
                Save
              </Box>
            }
          </Button>
        </Box>
      )}

      <Dialog
        open={showLogInDialog}
        onClose={() => setShowLogInDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent>
          <Authenticator customTitle={"Log in or sign up to do that"} />
        </DialogContent>
      </Dialog>

      <Collapse
        in={
          showLogInDialog ||
          (userProfile && Object.keys(userProfile).length !== 0)
        }
        mountOnEnter
        unmountOnExit
      >
        <Box
          sx={{
            margin: theme.spacing(3, "auto", 0, "auto"),
            width: "100%",
          }}
        >
          {userProfile && Object.keys(userProfile).length > 0 && (
            <Box>
              {!loanTermsUI &&
                answers
                  .filter((answer) => {
                    // filtering out the question that we don't want to show in this part of the UI
                    return allowedQuestionTitles.includes(answer.q_title);
                  })
                  .map((answer, index) => {
                    return (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          padding: theme.spacing(1),
                          borderTop: `none`,
                          borderTopRightRadius: 0,
                          borderTopLeftRadius: 0,
                          "& .MuiFormControl-root": {
                            margin: theme.spacing(1, 0, 0, "auto"),
                            display: "flex",
                            width: "100%",
                          },
                        }}
                      >
                        <Box sx={{ width: "100%" }}>
                          <Typography
                            variant="h2"
                            sx={{ fontSize: theme.spacing(3) }}
                          >
                            {answer.q_title}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            width: "100%",
                            display: "flex",
                            flexDirection: "row",
                          }}
                        >
                          <Box
                            sx={{
                              flex: "0 1 50%",
                              display: "flex",
                              flexDirection: "row",
                            }}
                          >
                            <Typography
                              variant="subtitle1"
                              sx={{
                                margin: theme.spacing("auto", 0),
                                fontSize: theme.spacing(1.8),
                              }}
                            >
                              {answer?.q_description &&
                                answer?.q_description(answers)}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              displat: "flex",
                              flex: "1 1 50%",
                            }}
                          >
                            {generateAnswerOptions(answer, index, answers)}
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  padding: theme.spacing(1),
                  "& .MuiFormControl-root": {
                    margin: theme.spacing(1, 0, 0, "auto"),
                    display: "flex",
                    width: "100%",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: theme.spacing(2),
                    justifyContent: "space-between",
                    padding: theme.spacing(1, 0),
                    position: "relative",
                    height: theme.spacing(8),
                    transformStyle: "preserve-3d",
                    [theme.breakpoints.down("sm")]: {
                      flexDirection: "column",
                      height: "auto",
                      gap: theme.spacing(2.5),
                      padding: theme.spacing(2, 0),
                    },
                  }}
                >
                  <Button
                    onClick={() => {
                      if (
                        !userProfile ||
                        Object.keys(userProfile).length === 0
                      ) {
                        setShowLogInDialog(true);
                        return;
                      }
                      const isPDFGenerated = true;
                      saveOrUpdate({
                        user,
                        userProfile,
                        answers,
                        calc,
                        calcType,
                        setCalc,
                        showUpdateFields,
                        setShowCreatedSuccessMessage,
                        setSnackbarData,
                        isPDFGenerated,
                        loanTermsUI,
                      });

                      handlePDFDownload(
                        setSnackbarData,
                        theme,
                        isMobile,
                        calcType
                      );
                    }}
                    color="info"
                    variant={"contained"}
                    sx={{
                      flex: 1,
                      fontWeight: 700,
                      boxShadow: 2,
                      transform: "none",
                      height: theme.spacing(6),
                      transformOrigin: "bottom center",
                      transition: "box-shadow 0.3s ease",
                      "&:hover": {
                        transform: "none",
                        boxShadow: 4,
                        zIndex: 2,
                        filter: "brightness(1.05)",
                        borderWidth: "2px",
                      },
                      [theme.breakpoints.down("md")]: {
                        width: "100%",
                        py: 1.5,
                      },
                      [theme.breakpoints.down("sm")]: {
                        minHeight: theme.spacing(7),
                      },
                    }}
                  >
                    <Box
                      sx={{
                        padding: theme.spacing(0.8),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "100%",
                      }}
                    >
                      {" "}
                      <PictureAsPdfIcon
                        sx={{ mr: 1, fontSize: { xs: "1.4rem", sm: "1.2rem" } }}
                      />{" "}
                      PDF
                    </Box>
                  </Button>
                  <Button
                    color={"primary"}
                    variant={"outlined"}
                    sx={{
                      flex: 1,
                      fontWeight: 700,
                      display: "flex",
                      textDecoration: "none !important",
                      borderWidth: 1,
                      boxShadow: 1,
                      transform: "none",
                      height: theme.spacing(6),
                      transformOrigin: "bottom center",
                      transition: "box-shadow 0.3s ease",
                      "&:hover": {
                        transform: "none",
                        boxShadow: 2,
                        zIndex: 2,
                        filter: "brightness(1.05)",
                        borderWidth: "2px",
                      },
                      [theme.breakpoints.down("md")]: {
                        width: "100%",
                        py: 1.5,
                      },
                      [theme.breakpoints.down("sm")]: {
                        minHeight: theme.spacing(7),
                      },
                    }}
                    onClick={async () => {
                      if (
                        !userProfile ||
                        Object.keys(userProfile).length === 0
                      ) {
                        setShowLogInDialog(true);
                        return;
                      }
                      const calc = await saveOrUpdate({
                        user,
                        userProfile,
                        answers,
                        calc,
                        calcType,
                        setCalc,
                        showUpdateFields,
                        setShowCreatedSuccessMessage,
                        setSnackbarData: false,
                      });

                      navigator.clipboard
                        .writeText(
                          `${
                            process.env.deployment === "dev"
                              ? "http://localhost:3000"
                              : "https://www.offermarket.us"
                          }/my/calculations?ref=${calc?.RefTag}`
                        )
                        .then(() => {
                          setShowCopiedSuccess(true);

                          setTimeout(function () {
                            setShowCopiedSuccess(false);
                          }, 10000);
                        });

                      setSnackbarData({
                        message: "Share link copied to clipboard",
                        options: {
                          variant: "success",
                          autoHideDuration: 5000,
                          anchorOrigin: {
                            vertical: "top",
                            horizontal: "center",
                          },
                        },
                      });
                    }}
                  >
                    {showCopiedSuccess ? (
                      <Box
                        sx={{
                          padding: theme.spacing(0.8),
                          fontWeight: 700,
                          width: "100%",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <>
                          <ContentCopyIcon size="lg" />
                          <Box
                            sx={{
                              margin: theme.spacing(1),
                            }}
                          >
                            Link copied
                          </Box>
                        </>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          width: "100%",
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            padding: theme.spacing(0.8),
                            fontWeight: 700,
                          }}
                        >
                          {" "}
                          <ShareOutlinedIcon
                            sx={{ fontSize: { xs: "1.4rem", sm: "1.2rem" } }}
                          />{" "}
                          <Box
                            sx={{
                              margin: theme.spacing(0, 1),
                            }}
                          >
                            Share
                          </Box>
                        </Box>
                      </Box>
                    )}
                  </Button>

                  {
                    <Button
                      onClick={() => {
                        if (
                          !userProfile ||
                          Object.keys(userProfile).length === 0
                        ) {
                          setShowLogInDialog(true);
                          return;
                        }
                        if (!loanTermsUI) {
                          saveOrUpdate({
                            user,
                            userProfile,
                            answers,
                            calc,
                            calcType,
                            setCalc,
                            showUpdateFields,
                            setShowCreatedSuccessMessage,
                            setSnackbarData,
                          });
                        } else {
                          // Fetch template and show explanation field

                          if (showExplanationField) {
                            setShowExplanationField(false);
                            return;
                          }

                          setShowExplanationField(true);
                          setLoadingTemplate(true);

                          (async () => {
                            try {
                              const template =
                                await getLoanTermsUpdateEmailTemplate(
                                  user,
                                  loan.id,
                                  answers // modified calc answers that haven't been saved yet
                                );

                              if (template && template.emailHTML) {
                                setEditorHtml(template.emailHTML);
                              }
                            } catch (error) {
                              console.error(
                                "Error fetching loan terms template:",
                                error
                              );
                            } finally {
                              setLoadingTemplate(false);
                            }
                          })();
                        }
                      }}
                      color="success"
                      variant={"contained"}
                      sx={{
                        flex: 1,
                        fontWeight: 700,
                        boxShadow: 2,
                        transform: "none",
                        height: theme.spacing(6),
                        transformOrigin: "bottom center",
                        transition: "box-shadow 0.3s ease",
                        "&:hover": {
                          transform: "none",
                          boxShadow: 3,
                          zIndex: 2,
                          filter: "brightness(1.05)",
                          borderWidth: "2px",
                        },
                        [theme.breakpoints.down("md")]: {
                          width: "100%",
                          py: 1.5,
                        },
                        [theme.breakpoints.down("sm")]: {
                          minHeight: theme.spacing(7),
                        },
                      }}
                    >
                      <Box
                        sx={{
                          padding: theme.spacing(0.8),
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "100%",
                        }}
                      >
                        {" "}
                        <SaveIcon
                          sx={{
                            mr: 1,
                            fontSize: { xs: "1.4rem", sm: "1.2rem" },
                          }}
                        />
                        {loanTermsUI ? "Update Loan Terms" : "Save"}
                      </Box>
                    </Button>
                  }
                </Box>
                {showCreatedSuccessMessage &&
                  (snackbarData.message.includes("copied") ? (
                    <Box>
                      <Alert
                        variant="outlined"
                        icon={
                          <Box>
                            <ShareOutlinedIcon size="lg" />
                          </Box>
                        }
                        sx={{
                          width: "100% !important",
                          "& .MuiAlert-message": {
                            width: "100% !important",
                          },
                          display: "flex",
                          margin: theme.spacing(0, 0, 1, 0),
                          color: "black !important",
                        }}
                        color={"info"}
                      >
                        <Box
                          sx={{
                            textAlign: "left",
                            fontSize: theme.spacing(3),
                            color: theme.palette.info.main,
                            display: "flex",
                          }}
                        >
                          Share your calculation{" "}
                        </Box>
                        <Box>
                          <TextField
                            sx={{
                              textAlign: "left",
                              width: "100%",
                              margin: theme.spacing(2, 0),
                            }}
                            label={
                              calc?.RefTag
                                ? "Share URL"
                                : "Press SAVE to generate a link"
                            }
                            variant="outlined"
                            value={
                              calc?.RefTag
                                ? `${
                                    process.env.deployment === "dev"
                                      ? "http://localhost:3000"
                                      : "https://www.offermarket.us"
                                  }/my/calculations?ref=${calc?.RefTag}`
                                : ""
                            }
                            InputLabelProps={{
                              shrink: calc?.RefTag
                                ? `${
                                    process.env.deployment === "dev"
                                      ? "http://localhost:3000"
                                      : "https://www.offermarket.us"
                                  }/my/calculations?ref=${calc?.RefTag}`
                                : "",
                            }}
                          />
                          <Box>
                            A business partner with your link will be able to
                            view and edit your calculation
                          </Box>
                        </Box>
                      </Alert>
                    </Box>
                  ) : (
                    <Alert variant="outlined" sx={{ margin: theme.spacing(1) }}>
                      {loanTermsUI
                        ? "New version of your Loan Terms has been saved!"
                        : "Your calculation has been saved!"}
                      {!loanTermsUI && (
                        <>
                          <br />
                          <br />
                          To update or compare your calculations, go to{" "}
                          <a href="/my/calculations">Calculations</a>{" "}
                        </>
                      )}
                    </Alert>
                  ))}
              </Box>
            </Box>
          )}
        </Box>
        {loanTermsUI && showExplanationField && (
          <Box sx={{ mt: 2, mb: 2 }}>
            {/* Alert with different messages based on user role */}
            <Alert
              severity="info"
              variant="outlined"
              sx={{
                mb: 2,
                "& .MuiAlert-message": {
                  width: "100%",
                  textAlign: "left",
                },
              }}
            >
              {user?.uid === loan?.UID
                ? 'Add a brief explanation in the "Explanation" column for your Loan Processor and to enable the "Save Changes" button.'
                : 'Add a brief explanation in the "Explanation" column for the client and to enable the "Save Changes" button.'}
            </Alert>

            {/* TODO, add this for OM_processor for better visibility into what is being sent */}
            <EmailEditor
              editorHtml={editorHtml}
              loadingTemplate={loadingTemplate}
              height={60}
              onContentChange={(content) => {
                setComment(content);
              }}
              title="Loan Terms Change Explanation"
            />
            {/* <Box
              sx={{
                textAlign: "left",
                overflow: "scroll",
                height: theme.spacing(30),
              }}
            >
              <MUIRichTextEditor
                key="stable-editor-key"
                defaultValue={initialEditorState.current}
                label="Please provide a reason for the Loan Terms change"
                inlineToolbar={true}
                onChange={(e) => {
                  const contentState = e.getCurrentContent();
                  const rawContent = convertToRaw(contentState);
                  const markup = draftToHtml(rawContent);
                  setExplanationText(markup);
                }}
                sx={{ mb: 2 }}
              />
            </Box> */}
            <Tooltip
              title={
                !comment.trim() || loadingTemplate
                  ? "Please provide a brief explanation of changes in the area above"
                  : ""
              }
              placement="top"
              arrow
            >
              <span>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={!comment.trim() || loadingTemplate}
                  onClick={async () => {
                    const newLoanCalc = await saveOrUpdate({
                      user,
                      userProfile,
                      answers,
                      calc: null, // triggers create new calc which we need in this case
                      calcType,
                      setCalc,
                      showUpdateFields,
                      setShowCreatedSuccessMessage,
                      setSnackbarData,
                      explanation: comment.trim(),
                      targetUserID: loan.UID,
                      loanTermsUI: loanTermsUI,
                      associatedLoanID: loan.id,
                    });

                    setShowExplanationField(false);
                    setComment("");
                    setEditorHtml("");

                    // Add the new calculation to the loan's LoanCalcs array
                    const updatedLoanCalcs = loan.LoanCalcs
                      ? [...loan.LoanCalcs, newLoanCalc.id]
                      : [newLoanCalc.id];

                    // Update the loan with the new calculation
                    const updatedLoan = await updateLoan2(user, loan.id, {
                      LoanCalcs: updatedLoanCalcs,
                    });

                    const targetElementId = `loan_terms_box`;
                    const targetElement =
                      document.getElementById(targetElementId);
                    if (targetElement) {
                      targetElement.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                      });
                    }

                    await sleep(2000);

                    // Trigger onVersionSelect with the new calculation ID
                    onVersionSelect(newLoanCalc.id, updatedLoan);
                  }}
                  sx={{
                    width: "100%",
                    py: 1.5,
                    fontWeight: 700,
                    mt: 2,
                  }}
                >
                  Save changes
                </Button>
              </span>
            </Tooltip>
          </Box>
        )}
      </Collapse>
    </Box>
  );
};

export default SaveSharePDF;
